import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Calendar, Clock, MapPin, Users } from 'lucide-react';

interface InterviewFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interview?: any;
  onSuccess?: () => void;
  applicantMode?: boolean;
  job?: any;
  application?: any;
}

export default function InterviewForm({ 
  open, 
  onOpenChange, 
  interview, 
  onSuccess,
  applicantMode = false,
  job,
  application
}: InterviewFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: '60',
    location: '',
    type: 'video',
    description: '',
    participants: [] as string[]
  });

  // Load interview data if editing
  useEffect(() => {
    if (interview) {
      setFormData({
        title: interview.title,
        date: interview.date,
        time: interview.time,
        duration: interview.duration,
        location: interview.location,
        type: interview.type,
        description: interview.description || '',
        participants: interview.participants || []
      });
    } else if (job && application) {
      // Set default title and participants for new interview
      setFormData(prev => ({
        ...prev,
        title: `Interview for ${job.title}`,
        participants: [application.profiles?.email].filter(Boolean)
      }));
    }
  }, [interview, job, application]);

  const handleSubmit = async () => {
    if (!user || !formData.date || !formData.time) return;

    setIsLoading(true);
    try {
      const interviewData = {
        profile_id: user.id,
        job_id: job?.id,
        application_id: application?.id,
        title: formData.title,
        date: formData.date,
        time: formData.time,
        duration: formData.duration,
        location: formData.location,
        type: formData.type,
        description: formData.description,
        participants: formData.participants,
        status: 'scheduled'
      };

      if (interview?.id) {
        // Update existing interview
        const { error } = await supabase
          .from('interviews')
          .update(interviewData)
          .eq('id', interview.id);

        if (error) throw error;
      } else {
        // Create new interview
        const { error } = await supabase
          .from('interviews')
          .insert([interviewData]);

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: interview?.id ? 'Interview updated successfully' : 'Interview scheduled successfully'
      });

      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving interview:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to save interview'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Schedule Interview</DialogTitle>
          <DialogDescription>
            {interview?.id ? 'Update interview details' : 'Schedule a new interview'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Interview Title</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Technical Interview for Senior Developer"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Time</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Duration</label>
            <Select
              value={formData.duration}
              onValueChange={(value) => setFormData(prev => ({ ...prev, duration: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type and Location */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Interview Type</label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                  <SelectItem value="onsite">On-site</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder={formData.type === 'onsite' ? 'Office address' : 'Meeting link'}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Participants */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Participants</label>
            <div className="relative">
              <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Textarea
                value={formData.participants.join('\n')}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  participants: e.target.value.split('\n').filter(Boolean)
                }))}
                placeholder="Enter email addresses (one per line)"
                className="min-h-[100px] pl-10"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Add any additional details or instructions..."
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isLoading || !formData.date || !formData.time || !formData.location}
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
            ) : null}
            {interview?.id ? 'Update Interview' : 'Schedule Interview'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}